// ── ACHCollectionForm ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { Btn, FInput, FInputNum, FSelect, Field, Modal, ReadOnlyForm } from "../../../shared/ui";
import { useForm } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

export const ACHCollectionForm = ({ initial, loanid, accountid, readOnly }) => {
  const { api, role, modal, setModal, closeModal, saving, error, saveRec } = useApp();
  const blank = {
    loanid: loanid || "", bankaccountid: "", underwriter: "",
    requested_ach_date: "", expected_ach_processed_date: "", ach_requested_amt: "",
    ach_collection_set_up_by: "", ach_collection_set_up_date: "", ach_set_up_amount: "",
    note: "", noteid: "",
  };
  const [f, set, setF] = useForm(initial || blank);

  // Re-read the record fresh from the DB whenever the form opens for an existing
  // one (the card's cached list row can be stale right after a save). Uses
  // getACHCollections so the joined display fields refresh too.
  useEffect(() => {
    const id = initial?.id;
    const lid = initial?.loanid || loanid;
    if (!id || !lid) return;
    api.get("getACHCollections?loanid=" + lid)
      .then(rows => { const fresh = (rows || []).find(r => String(r.id) === String(id)); if (fresh) setF(prev => ({ ...prev, ...fresh })); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [banks, setBanks] = useState([]);
  const [underwriters, setUnderwriters] = useState([]);
  const [setupUsers, setSetupUsers] = useState([]);
  const [loans, setLoans] = useState([]);

  const acctId = accountid || initial?.accountid;

  // Load option lists once.
  useEffect(() => {
    if (acctId) {
      api.get("getBankAccounts?accountid=" + acctId).then(rows => {
        const list = Array.isArray(rows) ? rows : [];
        setBanks(list);
        // Default a NEW record's bank account to the account's primary — but only
        // when there is exactly one primary (0 or >1 → leave null).
        if (!initial?.id) {
          const primaries = list.filter(b => b.primaryaccount);
          if (primaries.length === 1) set("bankaccountid")(primaries[0].id);
        }
      }).catch(() => {});
      if (!loanid) api.get("getLoans?accountid=" + acctId).then(r => setLoans(Array.isArray(r) ? r : [])).catch(() => {});
    }
    api.get("getUsers?filter=isunderwriter").then(r => setUnderwriters(Array.isArray(r) ? r : [])).catch(() => {});
    api.get("getUsers?filter=isdrawfunding").then(r => setSetupUsers(Array.isArray(r) ? r : [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userOpts = (arr) => [{ value: "", label: "— Select —" }, ...arr.map(u => ({ value: u.id, label: u.FullName }))];
  const bankOpts = [{ value: "", label: "— Select —" }, ...banks.map(b => ({
    value: b.id,
    label: (b.bankname || "Bank") + " ••••" + (b.accountnumberlast4 || "") + (b.primaryaccount ? " (Primary)" : ""),
  }))];
  const loanOpts = [{ value: "", label: "— Select —" }, ...loans.map(l => ({ value: l.id, label: l.loannumber }))];

  const selectedBank = banks.find(b => String(b.id) === String(f.bankaccountid));
  const bankLast4 = selectedBank ? selectedBank.accountnumberlast4 : (initial?.bank_acct_number || "");

  const canUW = !!role.isunderwriter;   // underwriter-only fields
  const canDF = !!role.isdrawfunding;   // draw-funding-only fields

  // ── Validation (mirrors the server) ──
  const missing = [];
  if (!(f.loanid || loanid)) missing.push("Loan");
  if (!f.bankaccountid) missing.push("Bank Account");
  if (!f.underwriter) missing.push("Underwriter");
  if (!f.requested_ach_date) missing.push("Requested ACH Date");
  if (!f.expected_ach_processed_date) missing.push("Expected ACH Processed Date");
  if (f.ach_requested_amt === "" || f.ach_requested_amt == null) missing.push("ACH Requested Amount");
  const amtMismatch = f.ach_collection_set_up_date &&
    (parseFloat(f.ach_requested_amt || 0) !== parseFloat(f.ach_set_up_amount || 0));
  // The missing-field list no longer prints as a standing paragraph — the red
  // asterisks say which fields are required, and repeating the same thing in
  // prose under the form is noise you learn to scroll past. It moves to the
  // disabled Save button's tooltip, where somebody who is stuck will look.
  // The amount mismatch DOES still print, because no asterisk can express
  // "these two figures have to agree once a Set Up Date exists".
  const blockedMsg = amtMismatch
    ? "ACH Requested Amount must equal ACH Set Up Amount once a Set Up Date is entered."
    : null;
  const blockSave = missing.length > 0 || !!amtMismatch;

  const ST = ({ title }) => <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight, gridColumn:"1/-1" }}>{title}</div>;

  return (
    <Modal wide title={readOnly ? "View ACH Collection" : initial ? "Edit ACH Collection" : "Add ACH Collection"} onClose={closeModal}
      onEdit={readOnly && role.canEdit ? () => { closeModal(); setModal({ type:"ach-edit", rec:initial, loanid, accountid, onDone:modal.onDone }); } : null}>
      <ReadOnlyForm readOnly={readOnly}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
          <ST title="General" />
          {loanid
            ? /* Supplied by the page, not chosen here — nothing for a person to
                 fill in, so no asterisk. */
              <Field label="Loan"><div style={{ padding:"7px 0", fontSize:13 }}>{initial?.loannumber || "This loan"}</div></Field>
            : <Field label="Loan" required={!readOnly}><FSelect value={f.loanid} onChange={set("loanid")} options={loanOpts} /></Field>}
          <Field label="Bank Account" required={!readOnly}><FSelect value={f.bankaccountid} onChange={set("bankaccountid")} options={bankOpts} /></Field>
          <Field label="Bank Acct # (last 4)"><div style={{ padding:"7px 0", fontSize:13, color:"var(--color-text-secondary)" }}>{bankLast4 ? "••••" + bankLast4 : "—"}</div></Field>
          <Field label="Fund Ownership"><div style={{ padding:"7px 0", fontSize:13, color:"var(--color-text-secondary)" }}>{f.fund_ownership || initial?.fund_ownership || "—"}</div></Field>

          <ST title="Underwriting" />
          <Field label="Underwriter" required={!readOnly}><FSelect value={f.underwriter} onChange={set("underwriter")} options={userOpts(underwriters)} /></Field>
          <Field label="ACH Requested Amount" required={!readOnly}><FInputNum value={f.ach_requested_amt} onChange={set("ach_requested_amt")} /></Field>
          <Field label="Requested ACH Date" required={!readOnly}><FInput value={f.requested_ach_date?.slice(0,10) || ""} onChange={set("requested_ach_date")} type="date" disabled={!canUW} /></Field>
          <Field label="Expected ACH Processed Date" required={!readOnly}><FInput value={f.expected_ach_processed_date?.slice(0,10) || ""} onChange={set("expected_ach_processed_date")} type="date" disabled={!canUW} /></Field>
          {!canUW && !readOnly && <div style={{ gridColumn:"1/-1", fontSize:11, color:"var(--color-text-secondary)", marginBottom:4 }}>Requested / Expected ACH dates are editable only by underwriters.</div>}

          <ST title="Set Up (Draw Funding)" />
          <Field label="Set Up By"><FSelect value={f.ach_collection_set_up_by} onChange={set("ach_collection_set_up_by")} options={userOpts(setupUsers)} disabled={!canDF} /></Field>
          <Field label="Set Up Amount"><FInputNum value={f.ach_set_up_amount} onChange={set("ach_set_up_amount")} disabled={!canDF} /></Field>
          <Field label="Set Up Date"><FInput value={f.ach_collection_set_up_date?.slice(0,10) || ""} onChange={set("ach_collection_set_up_date")} type="date" disabled={!canDF} /></Field>
          {!canDF && !readOnly && <div style={{ gridColumn:"1/-1", fontSize:11, color:"var(--color-text-secondary)", marginBottom:4 }}>Set Up fields are editable only by draw-funding users.</div>}

          <ST title="Note" />
          <div style={{ gridColumn:"1/-1" }}>
            <Field label="Note"><FInput value={f.note || ""} onChange={set("note")} /></Field>
          </div>
        </div>
      </ReadOnlyForm>

      {!readOnly && blockedMsg && <div style={{ color:"#993C1D", fontSize:12, marginTop:10 }}>{blockedMsg}</div>}
      {!readOnly && error && <div style={{ color:"#993C1D", fontSize:12, marginTop:8 }}>{error}</div>}

      <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:12 }}>
        <Btn onClick={closeModal}>{readOnly ? "Close" : "Cancel"}</Btn>
        {!readOnly && <Btn disabled={saving || blockSave}
          title={missing.length ? "Still needed: " + missing.join(", ") : undefined}
          onClick={() => saveRec("saveACHCollection", { ...f, loanid: f.loanid || loanid }, initial ? "Edited" : "Added", "ACH_Collection", (initial ? "Updated" : "Added") + " ACH collection", modal.onDone)}
          variant="primary">{saving ? "Saving..." : "Save"}</Btn>}
      </div>
    </Modal>
  );
};
